import { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Empty,
  Grid,
  Input,
  List,
  Modal,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  DownOutlined,
  PlusOutlined,
  ReloadOutlined,
  RightOutlined,
  SearchOutlined,
  TeamOutlined,
} from "@ant-design/icons";

import { PageHeader } from "../../../shared/components/PageHeader";
import { MobilePagination } from "../../../shared/components/MobilePagination";
import { appNotification } from "../../../shared/notifications/appNotification";
import { fetchProducts } from "../../../entities/product/api/productsApi";
import { fetchSellerOptions } from "../../../entities/seller/api/sellersApi";
import type { SellerOptionResponse } from "../../../entities/seller/model/types";
import {
  assignRecommendSellerProducts,
  bulkDeleteSellerProducts,
  fetchSellerProductOverview,
  setSellerProductLeader,
} from "../api/sellerProductsApi";
import { SellerProductForm } from "../components/SellerProductForm";
import type {
  SellerProductAssignment,
  SellerProductOverview,
} from "../model/types";
import {
  canSelectSellerProducts,
  emptySellerProductSelection,
  setSellerProductSelection,
  toggleSellerProductList,
  toggleSellerProductSelection,
  type SellerProductSelection,
} from "../model/selection";
import "../styles/SellerProducts.css";
import { usePageRefresh } from "../../../shared/pull-refresh/PullRefreshContext";

const { useBreakpoint } = Grid;
const { Text } = Typography;
const PRODUCT_PAGE_SIZE = 100;

const productLabel = (product: {
  product_code: string;
  product_name: string | null;
}) =>
  product.product_name
    ? `${product.product_code} - ${product.product_name}`
    : product.product_code;

const productNamesLabel = (assignments: SellerProductAssignment[]) =>
  assignments
    .map(
      ({ product }) =>
        product.product_name?.replace(/\s+/g, "") ||
        product.product_code?.trim(),
    )
    .join("、");

interface ProductAssignmentsProps {
  seller: SellerProductOverview["seller"];
  assignments: SellerProductAssignment[];
  selection: SellerProductSelection;
  saving: boolean;
  onToggleSelection: (
    seller: SellerProductOverview["seller"],
    sellerProductId: number | undefined,
    selected: boolean,
  ) => void;
  onSelectAll: (
    seller: SellerProductOverview["seller"],
    assignments: SellerProductAssignment[],
    selected: boolean,
  ) => void;
  onClearSelection: () => void;
  onOpenBulkDelete: (
    seller: SellerProductOverview["seller"],
    assignments: SellerProductAssignment[],
  ) => void;
  onSetLeader: (
    seller: SellerProductOverview["seller"],
    assignment: SellerProductAssignment,
  ) => void;
}

const ProductAssignments = ({
  seller,
  assignments,
  selection,
  saving,
  onToggleSelection,
  onSelectAll,
  onClearSelection,
  onOpenBulkDelete,
  onSetLeader,
}: ProductAssignmentsProps) => {
  const isSelectedSeller = selection.sellerId === seller.user_id;
  const isLocked = !canSelectSellerProducts(selection, seller.user_id);
  const selectedCount = isSelectedSeller
    ? assignments.filter((assignment) =>
        assignment.seller_product_id !== undefined &&
        selection.sellerProductIds.has(assignment.seller_product_id),
      ).length
    : 0;
  const allSelected =
    assignments.length > 0 && selectedCount === assignments.length;

  return (
    <section
      className="seller-product-assignments"
      aria-label={`${seller.display_name || seller.email}の商品`}
    >
      <div className="seller-product-assignments-header">
        <Text strong>担当商品</Text>
        <Space className="seller-product-selection-actions" size={4} wrap>
          <Text type="secondary">{selectedCount}件選択</Text>
          <Checkbox
            checked={allSelected}
            indeterminate={selectedCount > 0 && !allSelected}
            disabled={
              saving ||
              isLocked ||
              assignments.every((assignment) => assignment.seller_product_id === undefined)
            }
            onChange={(event) =>
              onSelectAll(seller, assignments, event.target.checked)
            }
          >
            すべて選択
          </Checkbox>
          <Button
            type="link"
            size="small"
            disabled={saving || !isSelectedSeller}
            onClick={onClearSelection}
          >
            選択解除
          </Button>
          <Button
            danger
            size="small"
            style={{ padding: 6 }}
            disabled={saving || !isSelectedSeller || selectedCount === 0}
            onClick={() => onOpenBulkDelete(seller, assignments)}
          >
            削除
          </Button>
        </Space>
      </div>
      {assignments.length ? (
        <div className="seller-product-assignment-list">
          {assignments.map((assignment) => (
            <div
              className="seller-product-assignment"
              key={assignment.product.product_code}
            >
              <Checkbox
                checked={
                  assignment.seller_product_id !== undefined &&
                  selection.sellerProductIds.has(assignment.seller_product_id)
                }
                disabled={saving || isLocked || assignment.seller_product_id === undefined}
                onChange={(event) =>
                  onToggleSelection(
                    seller,
                    assignment.seller_product_id,
                    event.target.checked,
                  )
                }
                aria-label={`${assignment.product.product_code}を選択`}
              />
              <div className="seller-product-assignment-copy">
                <Text strong>{assignment.product.product_code}</Text>
                {assignment.product.product_name && (
                  <Text type="secondary">
                    {assignment.product.product_name}
                  </Text>
                )}
                {assignment.sellers
                  .filter((peer) => peer.user_id !== seller.user_id)
                  .map((peer) => (
                    <Text
                      className="seller-product-peer"
                      key={peer.user_id}
                      type="secondary"
                    >
                      共同担当: {peer.display_name || peer.seller_code || peer.email}
                      {peer.is_leader ? "（主担当）" : ""}
                    </Text>
                  ))}
              </div>
              {assignment.is_leader ? (
                <Tag color="gold">主担当</Tag>
              ) : (
                <Button
                  size="small"
                  disabled={saving}
                  onClick={() => onSetLeader(seller, assignment)}
                >
                  主担当にする
                </Button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="担当商品はありません"
        />
      )}
    </section>
  );
};

export const SellerProductsPage = () => {
  const registerPageRefresh = usePageRefresh();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [items, setItems] = useState<SellerProductOverview[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selection, setSelection] = useState<SellerProductSelection>(
    emptySellerProductSelection,
  );
  const [expandedSellerId, setExpandedSellerId] = useState<number | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteModalSeller, setDeleteModalSeller] =
    useState<SellerOptionResponse>();
  const [deleteModalAssignments, setDeleteModalAssignments] = useState<
    SellerProductAssignment[]
  >([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sellerOptions, setSellerOptions] = useState<SellerOptionResponse[]>(
    [],
  );
  const [productOptions, setProductOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState<SellerOptionResponse>();
  const [sellerSearch, setSellerSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [productLoading, setProductLoading] = useState(false);
  const sellerSearchTimer = useRef<number | null>(null);
  const productSearchTimer = useRef<number | null>(null);
  const productPage = useRef(0);
  const productHasMore = useRef(false);
  const productRequestId = useRef(0);
  const productRequestInFlight = useRef(false);
  const productNames = useRef<Record<string, string>>({});

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchSellerProductOverview({
        page,
        page_size: pageSize,
        search: search || undefined,
      });
      setItems(
        response.items.map((record) => ({
          seller: {
            user_id: Number(record.seller.seller_code),
            display_name: record.seller.seller_name,
            email: "",
            seller_code: record.seller.seller_code,
            is_active: true,
          },
          product_count: record.product_count,
          products: record.products.map((assignment) => ({
            product: assignment.product,
            is_leader: assignment.is_leader,
            sellers: assignment.sellers.map((seller) => ({
              user_id: Number(seller.seller_code),
              display_name: seller.seller_name,
              email: "",
              seller_code: seller.seller_code,
              is_active: true,
              is_leader: seller.is_leader,
            })),
            created_at: assignment.created_at,
          })),
        })),
      );
      setTotal(response.pagination.total);
      if (
        page > 1 &&
        response.items.length === 0 &&
        response.pagination.total > 0
      ) {
        setPage(Math.max(response.pagination.total_pages, 1));
      }
    } catch {
      appNotification.error({ message: "データの取得に失敗しました" });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  const loadSellerOptions = useCallback(
    async (keyword = "") => {
      try {
        const sellers = await fetchSellerOptions(keyword, 100);
        setSellerOptions((previous) => {
          if (
            !selectedSeller ||
            sellers.some((seller) => seller.user_id === selectedSeller.user_id)
          )
            return sellers;
          const retained =
            previous.find(
              (seller) => seller.user_id === selectedSeller.user_id,
            ) || selectedSeller;
          return [retained, ...sellers];
        });
      } catch {
        appNotification.error({ message: "販売担当者の取得に失敗しました" });
      }
    },
    [selectedSeller],
  );

  const loadProductOptions = useCallback(
    async (keyword = "", append = false) => {
      if (append && (!productHasMore.current || productRequestInFlight.current))
        return;

      const requestId = productRequestId.current + 1;
      productRequestId.current = requestId;
      productRequestInFlight.current = true;
      setProductLoading(true);
      try {
        const nextPage = append ? productPage.current + 1 : 1;
        const response = await fetchProducts(
          nextPage,
          PRODUCT_PAGE_SIZE,
          keyword || undefined,
        );
        if (requestId !== productRequestId.current) return;

        const nextOptions = response.items.map((product) => ({
          value: product.product_code,
          label: productLabel(product),
        }));
        response.items.forEach((product) => {
          productNames.current[product.product_code] = product.product_name ?? "";
        });
        setProductOptions((previous) =>
          append
            ? [
                ...previous,
                ...nextOptions.filter(
                  (option) =>
                    !previous.some(
                      (previousOption) => previousOption.value === option.value,
                    ),
                ),
              ]
            : nextOptions,
        );
        productPage.current = response.pagination.page;
        productHasMore.current =
          response.pagination.page < response.pagination.total_pages;
      } catch {
        if (requestId === productRequestId.current) {
          appNotification.error({ message: "商品の取得に失敗しました" });
        }
      } finally {
        if (requestId === productRequestId.current) {
          productRequestInFlight.current = false;
          setProductLoading(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  useEffect(() => {
    registerPageRefresh({ enabled: true, refresh: loadRows });
    return () => registerPageRefresh(null);
  }, [registerPageRefresh, loadRows]);

  useEffect(() => {
    if (
      expandedSellerId !== null &&
      !items.some((item) => item.seller.user_id === expandedSellerId)
    ) {
      setExpandedSellerId(null);
      setSelection(emptySellerProductSelection());
    }
  }, [expandedSellerId, items]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!formOpen) return;
    void loadSellerOptions();
    void loadProductOptions();
  }, [formOpen, loadProductOptions, loadSellerOptions]);

  useEffect(() => {
    if (!formOpen) return;
    if (sellerSearchTimer.current)
      window.clearTimeout(sellerSearchTimer.current);
    sellerSearchTimer.current = window.setTimeout(() => {
      void loadSellerOptions(sellerSearch);
    }, 300);
    return () => {
      if (sellerSearchTimer.current)
        window.clearTimeout(sellerSearchTimer.current);
    };
  }, [formOpen, loadSellerOptions, sellerSearch]);

  useEffect(() => {
    if (!formOpen) return;
    if (productSearchTimer.current)
      window.clearTimeout(productSearchTimer.current);
    productSearchTimer.current = window.setTimeout(() => {
      void loadProductOptions(productSearch);
    }, 300);
    return () => {
      if (productSearchTimer.current)
        window.clearTimeout(productSearchTimer.current);
    };
  }, [formOpen, loadProductOptions, productSearch]);

  const openAssignmentForm = (seller?: SellerOptionResponse) => {
    setSelectedSeller(seller);
    setFormOpen(true);
  };

  const closeAssignmentForm = () => {
    setFormOpen(false);
    setSelectedSeller(undefined);
    setSellerSearch("");
    setProductSearch("");
  };

  const handleSubmit = async (values: {
    seller_id: number;
    product_codes: string[];
  }) => {
    setSaving(true);
    try {
      const seller =
        selectedSeller ??
        sellerOptions.find((option) => option.user_id === values.seller_id);
      if (!seller) throw new Error("販売担当者を選択してください");

      await assignRecommendSellerProducts(
        String(seller.user_id),
        {
          seller_name: seller.display_name || seller.email,
          products: values.product_codes.map((productCode) => ({
            product_code: productCode,
            product_name: productNames.current[productCode] ?? "",
          })),
        },
      );
      appNotification.success({
        message: "商品を販売担当者に割り当てました",
      });
      closeAssignmentForm();
      await loadRows();
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSelection = (
    seller: SellerProductOverview["seller"],
    sellerProductId: number | undefined,
    selected: boolean,
  ) => {
    if (sellerProductId === undefined) return;
    setSelection((previous) =>
      toggleSellerProductSelection(
        previous,
        seller.user_id,
        sellerProductId,
        selected,
      ),
    );
  };

  const handleSelectAll = (
    seller: SellerProductOverview["seller"],
    assignments: SellerProductAssignment[],
    selected: boolean,
  ) => {
    setSelection((previous) =>
      setSellerProductSelection(
        previous,
        seller.user_id,
        assignments.flatMap((assignment) =>
          assignment.seller_product_id === undefined
            ? []
            : [assignment.seller_product_id],
        ),
        selected,
      ),
    );
  };

  const handleClearSelection = () => {
    setSelection(emptySellerProductSelection());
  };

  const handleToggleSellerProductList = (sellerId: number) => {
    if (saving || deleteModalOpen) return;
    setExpandedSellerId(toggleSellerProductList(expandedSellerId, sellerId));
    handleClearSelection();
  };

  const handleSetLeader = async (
    seller: SellerProductOverview["seller"],
    assignment: SellerProductAssignment,
  ) => {
    setSaving(true);
    try {
      await setSellerProductLeader(
        assignment.product.product_code,
        String(seller.user_id),
      );
      appNotification.success({ message: "主担当を更新しました" });
      await loadRows();
    } catch {
      appNotification.error({ message: "主担当の更新に失敗しました" });
    } finally {
      setSaving(false);
    }
  };

  const isSellerProductListExpanded = (sellerId: number) =>
    expandedSellerId === sellerId;

  const openBulkDeleteModal = (
    seller: SellerProductOverview["seller"],
    assignments: SellerProductAssignment[],
  ) => {
    const selectedAssignments = assignments.filter((assignment) =>
      assignment.seller_product_id !== undefined &&
      selection.sellerProductIds.has(assignment.seller_product_id),
    );
    if (!selectedAssignments.length || selection.sellerId !== seller.user_id)
      return;
    setDeleteModalSeller(seller);
    setDeleteModalAssignments(selectedAssignments);
    setDeleteModalOpen(true);
  };

  const closeBulkDeleteModal = () => {
    if (saving) return;
    setDeleteModalOpen(false);
    setDeleteModalSeller(undefined);
    setDeleteModalAssignments([]);
  };

  const handleBulkDelete = async () => {
    if (!deleteModalSeller || !deleteModalAssignments.length) return;
    const sellerProductIds = deleteModalAssignments.flatMap((assignment) =>
      assignment.seller_product_id === undefined
        ? []
        : [assignment.seller_product_id],
    );
    if (!sellerProductIds.length) return;
    setSaving(true);
    try {
      const result = await bulkDeleteSellerProducts({
        seller_id: deleteModalSeller.user_id,
        seller_product_ids: sellerProductIds,
      });
      appNotification.success({
        message: `${result.deleted_count}件の紐付けを削除しました`,
      });
      setDeleteModalOpen(false);
      setDeleteModalSeller(undefined);
      setDeleteModalAssignments([]);
      handleClearSelection();
      await loadRows();
    } catch {
      appNotification.error({ message: "削除に失敗しました" });
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<SellerProductOverview> = [
    {
      title: "販売担当者",
      key: "seller",
      minWidth: 320,
      render: (_, record) => (
        <div className="seller-product-seller-cell">
          <Tooltip title={record.seller.display_name || record.seller.email}>
            <Text strong>{record.seller.display_name || "--"}</Text>
          </Tooltip>
          <Text type="secondary">
            {record.seller.seller_code || record.seller.email}
          </Text>
        </div>
      ),
    },
    {
      title: "商品名",
      key: "product_names",
      minWidth: 300,
      render: (_, record) => {
        const productNames = productNamesLabel(record.products);
        return productNames ? (
          <Tooltip title={productNames}>
            <span className="seller-product-names">{productNames}</span>
          </Tooltip>
        ) : (
          "--"
        );
      },
    },
    {
      title: "担当商品",
      dataIndex: "product_count",
      width: 150,
      align: "center",
      render: (count: number) => (
        <Tag color={count ? "blue" : "default"}>{count} 件</Tag>
      ),
    },
    {
      title: "操作",
      width: 150,
      align: "center",
      render: (_, record) => (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openAssignmentForm(record.seller)}
        >
          商品を追加
        </Button>
      ),
    },
  ];

  const assignmentProps = (
    record: SellerProductOverview,
  ): ProductAssignmentsProps => ({
    seller: record.seller,
    assignments: record.products,
    selection,
    saving,
    onToggleSelection: handleToggleSelection,
    onSelectAll: handleSelectAll,
    onClearSelection: handleClearSelection,
    onOpenBulkDelete: openBulkDeleteModal,
    onSetLeader: (seller, assignment) =>
      void handleSetLeader(seller, assignment),
  });

  return (
    <div className="seller-products-page">
      <PageHeader
        title="販売担当商品設定"
        description="販売担当者ごとに担当商品を確認・管理します。"
        actionLabel={
          <>
            <PlusOutlined /> 商品を割り当て
          </>
        }
        onAction={() => openAssignmentForm()}
      />

      <Card className="seller-products-card" bordered={false}>
        <div className="seller-products-toolbar">
          <Input
            allowClear
            size="large"
            prefix={<SearchOutlined />}
            placeholder={
              isMobile
                ? "担当者・商品を検索"
                : "販売担当者コード・名前・商品コード・商品名で検索"
            }
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
          <Space className="seller-products-toolbar-actions">
            <div className="seller-products-total">
              <TeamOutlined /> <strong>{total}</strong> 名の販売担当者
            </div>
            {!isMobile && (
              <Button
                size="large"
                icon={<ReloadOutlined />}
                loading={loading}
                onClick={() => void loadRows()}
              >
                更新
              </Button>
            )}
          </Space>
        </div>

        {isMobile ? (
          <List
            className="seller-products-mobile-list"
            loading={loading}
            dataSource={items}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    search
                      ? "検索条件に一致する販売担当者がいません"
                      : "販売担当者がいません"
                  }
                />
              ),
            }}
            renderItem={(record) => (
              <List.Item className="seller-products-mobile-list-item">
                <article
                  className={`seller-products-mobile-card${
                    isSellerProductListExpanded(record.seller.user_id)
                      ? " seller-products-mobile-card--expanded"
                      : ""
                  }`}
                >
                  <div className="seller-products-mobile-header">
                    <div className="seller-product-seller-cell">
                      <Text strong>{record.seller.display_name || "--"}</Text>
                      <Text type="secondary">
                        {record.seller.seller_code || record.seller.email}
                      </Text>
                    </div>
                    <Space size={4}>
                      <Tag color={record.product_count ? "blue" : "default"}>
                        {record.product_count} 件
                      </Tag>
                      <Button
                        type="text"
                        className="seller-product-expand-button"
                        icon={
                          isSellerProductListExpanded(record.seller.user_id) ? (
                            <DownOutlined />
                          ) : (
                            <RightOutlined />
                          )
                        }
                        disabled={saving || deleteModalOpen}
                        aria-label={
                          isSellerProductListExpanded(record.seller.user_id)
                            ? "商品一覧を閉じる"
                            : "商品一覧を開く"
                        }
                        onClick={() =>
                          handleToggleSellerProductList(record.seller.user_id)
                        }
                      />
                    </Space>
                  </div>
                  {isSellerProductListExpanded(record.seller.user_id) && (
                    <ProductAssignments {...assignmentProps(record)} />
                  )}
                </article>
              </List.Item>
            )}
          />
        ) : (
          <Table
            rowKey={(record) => record.seller.user_id}
            loading={loading}
            columns={columns}
            dataSource={items}
            tableLayout="fixed"
            expandable={{
              expandedRowKeys:
                expandedSellerId === null ? [] : [expandedSellerId],
              expandedRowRender: (record) => (
                <ProductAssignments {...assignmentProps(record)} />
              ),
              rowExpandable: () => true,
              columnWidth: 52,
              expandIcon: ({ expanded, record }) => (
                <Tooltip
                  title={expanded ? "商品一覧を閉じる" : "商品一覧を開く"}
                >
                  <Button
                    type="text"
                    className="seller-product-expand-button"
                    icon={expanded ? <DownOutlined /> : <RightOutlined />}
                    aria-label={
                      expanded ? "商品一覧を閉じる" : "商品一覧を開く"
                    }
                    disabled={saving || deleteModalOpen}
                    onClick={() =>
                      handleToggleSellerProductList(record.seller.user_id)
                    }
                  />
                </Tooltip>
              ),
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    search
                      ? "検索条件に一致する販売担当者がいません"
                      : "販売担当者がいません"
                  }
                />
              ),
            }}
            pagination={{
              current: page,
              pageSize,
              total,
              size: "middle",
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (value, range) =>
                `${value} 名中 ${range[0]}-${range[1]} 名を表示`,
              onChange: (nextPage, nextSize) => {
                setPage(nextSize !== pageSize ? 1 : nextPage);
                setPageSize(nextSize);
              },
            }}
          />
        )}

        {isMobile && (
          <MobilePagination
            current={page}
            pageSize={pageSize}
            total={total}
            onChange={setPage}
          />
        )}
      </Card>

      <SellerProductForm
        open={formOpen}
        loading={saving || productLoading}
        sellerOptions={sellerOptions}
        productOptions={productOptions}
        initialSellerId={selectedSeller?.user_id}
        onCancel={closeAssignmentForm}
        onSubmit={handleSubmit}
        onSearchSeller={setSellerSearch}
        onSearchProduct={setProductSearch}
        onLoadMoreProducts={() => void loadProductOptions(productSearch, true)}
        hasMoreProducts={productHasMore.current}
      />

      <Modal
        open={deleteModalOpen}
        title="選択した担当商品の削除"
        okText="削除"
        cancelText="キャンセル"
        okButtonProps={{ danger: true, disabled: saving }}
        cancelButtonProps={{ disabled: saving }}
        confirmLoading={saving}
        closable={!saving}
        maskClosable={!saving}
        keyboard={!saving}
        onCancel={closeBulkDeleteModal}
        onOk={() => void handleBulkDelete()}
      >
        {deleteModalSeller && (
          <>
            <p>
              {deleteModalSeller.display_name || deleteModalSeller.email}
              {deleteModalSeller.display_name &&
                `（${deleteModalSeller.email}）`}
              の{deleteModalAssignments.length}件の商品紐付けを削除します。
            </p>
            <ul className="seller-product-delete-list">
              {deleteModalAssignments.map((assignment) => (
                <li key={assignment.product.product_code}>
                  {productLabel(assignment.product)}
                </li>
              ))}
            </ul>
            <Text type="danger">この操作は取り消せません。</Text>
          </>
        )}
      </Modal>
    </div>
  );
};

export default SellerProductsPage;
